import { RegionIdMap } from '@/view-models/region'
import { abbrLocaleToZhEnMap } from '@/view-models/common'
import { getPathWithoutLang } from '@/utils/middlewareHelpers'
import { useExternalLinks, usePartnerLinks } from '@/composition/useConst'

export default defineNuxtRouteMiddleware((to, from) => {
  const nuxtApp = useNuxtApp()
  const config = useRuntimeConfig()
  const { locale, localeProperties } = useGlobalI18n()
  const query = to.query

  const pathWithoutLang = getPathWithoutLang(to.path, locale.value)

  const abbrLocale = localeProperties.value['iso-abbrev']

  // paths below are for deep link
  if (pathWithoutLang === '/member_exclusive') {
    const url = `${config.public.EVENT_WEBSITE_URL}/membership-${abbrLocaleToZhEnMap[abbrLocale]}`
    return navigateTo(url, { redirectCode: 301 })
  }

  const homePaths = [
    '/tab/member',
    '/tab/news',
    '/new',
    '/coupon',
    '/mission',
    '/regions_list',
    '/settings',
    '/vip_points',
    '/asiamiles',
    '/referral',
    '/favorites_list',
    '/notifications_list',
    '/credit_cards_list',
    '/phone_verification',
  ]
  if (homePaths.includes(pathWithoutLang)) {
    return navigateTo(nuxtApp.$localePath({ name: 'index', query }), { redirectCode: 301 })
  }
  if (pathWithoutLang === '/tab/home') {
    const { region_id, ...restQuery } = query
    if (region_id) {
      return navigateTo(nuxtApp.$localePath({ name: 'regions-rid', params: { rid: region_id }, query: restQuery }), {
        redirectCode: 301,
      })
    }
    return navigateTo(nuxtApp.$localePath({ name: 'index', query }), { redirectCode: 301 })
  }
  if (pathWithoutLang === '/home') {
    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid', params: { rid: RegionIdMap.Taipei }, query }), { redirectCode: 301 })
  }
  const promoCodePaths = ['/tab/promotion', '/promotion']
  if (promoCodePaths.includes(pathWithoutLang)) {
    return navigateTo(nuxtApp.$localePath({ name: 'member-promo-code', query }), { redirectCode: 301 })
  }

  const orderListPaths = ['/tab/orders_list', '/orders_list']
  if (orderListPaths.includes(pathWithoutLang)) {
    return navigateTo(nuxtApp.$localePath({ name: 'orders-list', query }), { redirectCode: 301 })
  }

  const branchPathRegex = /^\/branches_list\/[0-9]+$/
  if (pathWithoutLang.match(branchPathRegex)) {
    let bid = pathWithoutLang.split('/branches_list/')[1]
    bid = query.bid ? query.bid : bid
    return navigateTo(nuxtApp.$localePath({ name: 'branches-id', params: { id: bid }, query }), { redirectCode: 301 })
  }
  const groupPathRegex = /^\/order_combination$/
  if (pathWithoutLang.match(groupPathRegex)) {
    const bid = query.bid
    const pid = query.pid
    const routeQuery = {
      ...(query.code ? { code: query.code } : null),
      ...(query.booking_time ? { booking_time: query.booking_time } : null),
      ...(query.group_code ? { group_code: query.group_code } : null),
      ...(query.nickname ? { nickname: query.nickname } : null),
      ...(query.count ? { count: query.count } : null),
    }
    // pid is new query string for group order
    const destinationUrl = pid
      ? nuxtApp.$localePath({ name: 'products-id', params: { id: pid }, query: routeQuery })
      : nuxtApp.$localePath({ name: 'branches-id', params: { id: bid }, query: routeQuery })
    return navigateTo(destinationUrl, { redirectCode: 301 })
  }

  const faqPaths = [/^\/help_list\/[0-9]+$/, /^\/help_list\/?$/]
  if (faqPaths.find(path => pathWithoutLang.match(path))) {
    return navigateTo(nuxtApp.$localePath({ name: 'faq', query }), { redirectCode: 301 })
  }

  const categoryPaths = [/^\/categories_list\/[0-9]+$/]
  if (categoryPaths.find(path => pathWithoutLang.match(path))) {
    const rid = query.regionid ? query.regionid : ''
    const cid = pathWithoutLang.split('/categories_list/')[1]

    if (rid && cid) {
      return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-categories-id', params: { rid: rid, id: cid }, query }), { redirectCode: 301 })
    } else {
      return navigateTo('/error?status_code=404')
    }
  }

  const searchPaths = [/^\/search\/.+$/]
  if (searchPaths.find(path => pathWithoutLang.match(path))) {
    const rid = query.regionid ? query.regionid : RegionIdMap.Taipei
    const keyword = decodeURI(pathWithoutLang.split('/search/')[1])

    const routeQuery = {
      filter_keyword: keyword,
    }

    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-search', params: { rid: rid }, query: routeQuery }), { redirectCode: 301 })
  }

  const orderDetailPaths = [/^\/orders_list\/[0-9]+$/, /^\/orders_list\/[0-9]+\/redeem$/]
  if (orderDetailPaths.find(path => pathWithoutLang.match(path))) {
    const orderId = pathWithoutLang.split('/orders_list/')[1].split('/')[0]

    if (!orderId) {
      return navigateTo(nuxtApp.$localePath({ name: 'orders-list', query }), { redirectCode: 301 })
    } else {
      return navigateTo(nuxtApp.$localePath({ name: 'orders-info-order_id', params: { order_id: orderId }, query }), { redirectCode: 301 })
    }
  }

  // paths below are for external website
  if (pathWithoutLang === '/joinus') {
    const { partnerLink } = usePartnerLinks()
    return navigateTo(partnerLink.value, { redirectCode: 302, external: true })
  }

  if (pathWithoutLang === '/why_funnow') {
    const { whyFunNowLink } = useExternalLinks()
    return navigateTo(whyFunNowLink.value, { redirectCode: 302, external: true })
  }

  if (pathWithoutLang === '/brand_story') {
    const { brandStoryLink } = useExternalLinks()
    return navigateTo(brandStoryLink.value, { redirectCode: 302, external: true })
  }

  // paths below are for old version paths redirection
  const termsOldPaths = [/^\/privacy$/, /^\/refund$/]
  if (termsOldPaths.find(path => pathWithoutLang.match(path))) {
    const termsType = pathWithoutLang.split('/')[1]
    return navigateTo(nuxtApp.$localePath({ name: `terms-${termsType}`, query }), { redirectCode: 301 })
  }

  const contactOldPaths = [/^\/contactus$/, /^\/contact\/[a-z_]+$/]
  if (contactOldPaths.find(path => pathWithoutLang.match(path))) {
    return navigateTo(nuxtApp.$localePath({ name: 'contact', query }), { redirectCode: 301 })
  }

  const categoryOldPaths = [/^\/categories\/[0-9]+-[0-9]+$/]
  if (categoryOldPaths.find(path => pathWithoutLang.match(path))) {
    const rid = pathWithoutLang.split('/categories/')[1].split('-')[0]
    const cid = pathWithoutLang.split('/categories/')[1].split('-')[1]

    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-categories-id', params: { rid: rid, id: cid }, query }), { redirectCode: 301 })
  }

  const themeOldPaths = [/^\/themes\/[0-9]+-[0-9]+$/]
  if (themeOldPaths.find(path => pathWithoutLang.match(path))) {
    const rid = pathWithoutLang.split('/themes/')[1].split('-')[0]
    const tid = pathWithoutLang.split('/themes/')[1].split('-')[1]

    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-themes-id', params: { rid: rid, id: tid }, query }), { redirectCode: 301 })
  }

  const branchOldPaths = [/^\/branch\/[0-9]+$/, /^\/branch\/[0-9]+-.+$/]
  if (branchOldPaths.find(path => pathWithoutLang.match(path))) {
    const bid = pathWithoutLang.split('/branch/')[1].split('-')[0]
    return navigateTo(nuxtApp.$localePath({ name: 'branches-id', params: { id: bid }, query }), { redirectCode: 301 })
  }

  const productOldPaths = [/^\/product\/[0-9]+$/]
  if (productOldPaths.find(path => pathWithoutLang.match(path))) {
    const pid = pathWithoutLang.split('/product/')[1]
    return navigateTo(nuxtApp.$localePath({ name: 'products-id', params: { id: pid }, query }), { redirectCode: 301 })
  }

  if (pathWithoutLang === '/member') {
    return navigateTo(nuxtApp.$localePath({ name: 'member-promo-code', query }), { redirectCode: 301 })
  }

  if (pathWithoutLang === '/orders') {
    return navigateTo(nuxtApp.$localePath({ name: 'orders-list', query }), { redirectCode: 301 })
  }

  if (pathWithoutLang === '/search') {
    const rid = query.region_id || RegionIdMap.Taipei
    const cid = query.category_id
    const keyword = query.keyword

    const routeQuery = {
      ...(query.time === 'now' ? { filter_time: query.time } : null),
      filter_keyword: keyword,
    }

    if (rid && cid) {
      return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-categories-id', params: { rid: rid, id: cid }, query: routeQuery }), {
        redirectCode: 301,
      })
    }

    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-search', params: { rid: rid }, query: routeQuery }), { redirectCode: 301 })
  }

  // TODO: 之後可能會轉到 tag 頁
  const tagPaths = [/^\/tags_list\/.+$/]
  if (tagPaths.find(path => pathWithoutLang.match(path))) {
    const rid = query.regionid || RegionIdMap.Taipei
    const cid = query.category_id
    const keyword = query.keyword

    const routeQuery = {
      ...(query.time === 'now' ? { filter_time: query.time } : null),
      filter_keyword: keyword,
    }

    if (rid && cid) {
      return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-categories-id', params: { rid: rid, id: cid }, query: routeQuery }), {
        redirectCode: 301,
      })
    }

    return navigateTo(nuxtApp.$localePath({ name: 'regions-rid-search', params: { rid: rid }, query: routeQuery }), { redirectCode: 301 })
  }

  // path below is to make the two paths(/funnowapp and /app) follow the same logic
  if (pathWithoutLang === '/funnowapp') {
    return navigateTo(nuxtApp.$localePath({ name: 'app', query }), { redirectCode: 301 })
  }
})
